/**
 * BillingInfoPage — Klant bedrijfs- en betaalgegevens invullen (FASE 5).
 *
 * Vereist VOORDAT iemand kan checkouten voor een abonnement:
 *  - Bedrijfsnaam, adres, postcode, plaats, KvK, telefoon
 *  - SEPA-toestemming akkoord
 *
 * Toont een progress-indicator + waarschuwing als velden ontbreken.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building, MapPin, Phone, Hash, FileText, Save, Loader2,
  CheckCircle, AlertCircle, ShieldCheck, ArrowRight,
} from 'lucide-react';

export default function BillingInfoPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [acceptSepa, setAcceptSepa] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api('get', '/api/platform/billing-info');
      setData(r.data);
      setForm({
        company_name: r.data.company.name || '',
        address: r.data.company.address || '',
        postal_code: r.data.company.postal_code || '',
        city: r.data.company.city || '',
        country: r.data.company.country || 'Nederland',
        phone: r.data.company.phone || '',
        kvk_number: r.data.company.kvk_number || '',
        vat_number: r.data.company.vat_number || '',
        iban: r.data.company.iban || '',
        bic: r.data.company.bic || '',
      });
      setAcceptSepa(r.data.consent.sepa_accepted);
    } catch (e) {
      toast.error('Laden mislukt');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const body = { ...form };
      if (acceptSepa && !data?.consent?.sepa_accepted) {
        body.accept_sepa_consent = true;
      }
      const r = await api('patch', '/api/platform/billing-info', body);
      setData((d) => ({ ...d, company: r.data.company, missing_required: r.data.missing_required, is_complete_for_checkout: r.data.is_complete_for_checkout }));
      toast.success(r.data.is_complete_for_checkout ? 'Gegevens compleet — u kunt nu een abonnement starten' : 'Opgeslagen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Opslaan mislukt');
    }
    setSaving(false);
  };

  if (!data) return <div style={{ padding: 60, textAlign: 'center' }}><Loader2 className="animate-spin" /></div>;

  const ok = data.is_complete_for_checkout;
  const accepted = data.consent.sepa_accepted;

  return (
    <div data-testid="billing-info-page" style={{ maxWidth: 760, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <p style={{ fontSize: 11, color: '#D4A017', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Mijn gegevens</p>
        <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 26, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
          Bedrijfs- en betaalgegevens
        </h1>
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
          Deze gegevens verschijnen op uw facturen en zijn nodig voor SEPA-incasso. Verplicht voor abonnementen.
        </p>
      </div>

      {/* Status banner */}
      {!ok ? (
        <div data-testid="billing-incomplete" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '4px solid #f59e0b', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10 }}>
          <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>Nog niet compleet</p>
            <p style={{ fontSize: 12, color: '#78350f' }}>Vul aan: {data.missing_required.join(', ')}</p>
          </div>
        </div>
      ) : (
        <div data-testid="billing-complete" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #22c55e', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <CheckCircle size={18} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>Compleet</p>
              <p style={{ fontSize: 12, color: '#166534' }}>U kunt nu een abonnement starten via checkout.</p>
            </div>
          </div>
          <Link to="/platform/checkout" data-testid="billing-to-checkout" style={{ background: '#16a34a', color: 'white', fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Naar checkout <ArrowRight size={12} />
          </Link>
        </div>
      )}

      <form onSubmit={save} style={{ background: 'white', borderRadius: 16, padding: '24px 22px', border: '1px solid #f1f5f9' }}>
        <Section label="Bedrijfsgegevens" icon={Building}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <Field label="Bedrijfsnaam *">
              <input data-testid="bi-company-name" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} style={inputStyle} />
            </Field>
            <Field label="Telefoon *">
              <input data-testid="bi-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+31 6 ..." style={inputStyle} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            <Field label="KvK-nummer *">
              <input data-testid="bi-kvk" required value={form.kvk_number} onChange={(e) => setForm({ ...form, kvk_number: e.target.value })} placeholder="12345678" style={inputStyle} />
            </Field>
            <Field label="BTW-nummer (optioneel)">
              <input data-testid="bi-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="NL123456789B01" style={inputStyle} />
            </Field>
          </div>
        </Section>

        <Section label="Adres" icon={MapPin}>
          <Field label="Adres + huisnummer *">
            <input data-testid="bi-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 10, marginTop: 10 }}>
            <Field label="Postcode *">
              <input data-testid="bi-postcode" required value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="1234 AB" style={inputStyle} />
            </Field>
            <Field label="Plaats *">
              <input data-testid="bi-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} style={inputStyle} />
            </Field>
            <Field label="Land">
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} style={inputStyle} />
            </Field>
          </div>
        </Section>

        <Section label="SEPA-incasso (optioneel — vooruit invullen)" icon={ShieldCheck}>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
            Stripe verzamelt uw IBAN automatisch tijdens de eerste betaling als u SEPA kiest. U kunt het hier ook nu al invullen.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <Field label="IBAN">
              <input data-testid="bi-iban" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })} placeholder="NL00 INGB 0000 0000 00" style={{ ...inputStyle, fontFamily: 'monospace' }} />
            </Field>
            <Field label="BIC">
              <input data-testid="bi-bic" value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value.toUpperCase() })} placeholder="INGBNL2A" style={{ ...inputStyle, fontFamily: 'monospace' }} />
            </Field>
          </div>
        </Section>

        {/* SEPA consent */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginTop: 14 }}>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input data-testid="bi-sepa-consent" type="checkbox" checked={acceptSepa || accepted} disabled={accepted} onChange={(e) => setAcceptSepa(e.target.checked)} style={{ width: 18, height: 18, marginTop: 2, accentColor: '#D4A017' }} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                Ik machtig <em>IMPERIA AI</em> tot doorlopende SEPA-incasso via Stripe
                {accepted && <span style={{ marginLeft: 8, fontSize: 11, color: '#16a34a' }}>✓ Geaccepteerd op {data.consent.sepa_accepted_at?.slice(0, 10)}</span>}
              </p>
              <p style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.6 }}>
                Betalingen worden veilig verwerkt via Stripe (PCI-DSS gecertificeerd). Voor SEPA-incasso wordt uw bankrekening
                op de overeengekomen vervaldatum belast. U kunt deze afschrijving binnen 56 dagen kosteloos terugboeken via uw bank.
              </p>
            </div>
          </label>
        </div>

        <button type="submit" disabled={saving} data-testid="bi-save" style={{ marginTop: 18, width: '100%', background: ok ? '#16a34a' : '#D4A017', color: 'white', fontWeight: 800, padding: '12px 16px', borderRadius: 10, border: 'none', cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: saving ? 0.7 : 1 }}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Opslaan…' : (ok ? 'Wijzigingen opslaan' : 'Gegevens opslaan')}
        </button>
      </form>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'block' }}>{label}</label>
      {children}
    </div>
  );
}

function Section({ label, icon: Icon, children }) {
  return (
    <div style={{ marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid #f1f5f9' }}>
      <p style={{ fontSize: 11, fontWeight: 800, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
        <Icon size={12} /> {label}
      </p>
      {children}
    </div>
  );
}
